import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/UserContextProvider";
import i18n from "../../../language/i18n";
import {
  BaseLoading,
  EmptyState,
  SecondaryButton,
  PrimaryButton,
  Icon,
} from "../../../library";
import {
  retrieveSubjectStyle,
  retrieveSubjectCardBackground,
} from "../../../utilization/SubjectUtilization";
import { retrieveAssignmentStatusKind } from "../../../utilization/AssignmentStatusUtilization";
import { buildCategoryLookup } from "../../../utilization/CategoryThemeUtilization";
import { personalizedAssignmentCardTopicOf } from "../../../utilization/PersonalizedAssignmentTriggerUtilization";
import { homeworkStyles } from "./HomeworkStyles";
import SubjectIcon from "../achievements/SubjectIcon";
import LiveCountdown from "../LiveCountdown";
import AssignmentKindChip from "../../learner/assignments/AssignmentKindChip";

interface HomeworkProps {
  contentList: any[];
  language?: string;
  loading?: boolean;
  dataTour?: string;
}

// The design system's HwCard: one card titled "Ödevler" with a vertical list of
// horizontal subject-coded rows (pill + [kind rail / subject / topic] + progress + due + CTA).
// Since Çalışmalar was folded in, the list carries all FOUR ödev kinds, which is why each
// row states its kind on a meta rail above the ders.
const Homework = (props: HomeworkProps) => {
  const navigate = useNavigate();
  const { contentList, language, loading, dataTour } = props;
  const currentTimestamp = new Date().getTime();
  // Bumped when a row's begin-countdown hits zero so it flips from "Yakında" to an
  // actionable "Başla" state without a manual refresh.
  const [, setUnlockTick] = useState(0);
  // A direct-sent bireysel ödev is named after its outcome's theme, resolved from this list.
  const { categoryDTOList } = useAuth();
  const categoryLookup = useMemo(
    () => buildCategoryLookup(categoryDTOList),
    [categoryDTOList],
  );

  const renderRow = (activeContent: any) => {
    const { id, type, beginAt, endAt, completionStatus, completionPercentage } =
      activeContent;
    let coterieType;
    let name;
    let total = 0;
    if (type === "BRANCH_ASSIGNMENT") {
      coterieType = activeContent.coterieType;
      name = activeContent.name;
      // The learner bootstrap returns a scalar activityCount and leaves activityDTOList
      // empty (perf: it no longer loads full activity content). Prefer the scalar; fall
      // back to the list length for full-DTO paths that still populate activityDTOList.
      total =
        activeContent.activityCount ?? activeContent.activityDTOList?.length ?? 0;
    } else if (type === "STANDARD_EXAM") {
      const cse = activeContent.standardExamDTO;
      coterieType = cse?.standardExamType;
      name = cse?.name;
      total = (cse?.standardExamPartDTOList || []).reduce(
        (sum: number, p: any) =>
          sum + (p.standardExamActivityDTOList?.length || 0),
        0,
      );
    } else if (type === "SUPPORT_ASSIGNMENT") {
      // Normalized in LearnerApp (normalizeSupportAssignmentContent), so activityCount and
      // completionPercentage/-Status already read like every other row here.
      coterieType = activeContent.coterieType;
      name = activeContent.name || i18n.t("assignment_kind_support");
      total = activeContent.activityCount ?? 0;
    } else if (type === "PERSONALIZED_ASSIGNMENT") {
      // A teacher-sent bireysel ödev (şube-ödevi-hung or direct). It used to fall through every
      // branch here: no name, "0 / 0", an Invalid Date, and a Başla that navigated nowhere.
      coterieType = activeContent.coterieType;
      // The kind rail already says "Bireysel ödev", so the topic never repeats it.
      name = personalizedAssignmentCardTopicOf(activeContent, categoryLookup);
      total = activeContent.activityDTOList?.length ?? 0;
    }
    const s = retrieveSubjectStyle(coterieType);
    const pct = Math.round((completionPercentage || 0) * 100);
    const done = Math.round((completionPercentage || 0) * total);
    // Window-aware status: a future beginAt makes the row "upcoming" (Yakında), which
    // replaces the misleading "Başlanılmadı" badge and swaps the due date for a
    // "Başlamasına …" opening countdown; a past endAt makes it "expired" (Süresi doldu).
    const statusKind = retrieveAssignmentStatusKind(
      completionStatus,
      beginAt,
      endAt,
      currentTimestamp,
    );
    const isUpcoming = statusKind === "upcoming";
    const isExpired = statusKind === "expired";
    const endDate = new Date(endAt);
    const goToContent = () => {
      if (type === "BRANCH_ASSIGNMENT") {
        navigate(`/branch-assignments/${id}`);
      } else if (type === "STANDARD_EXAM") {
        navigate(`/standard-exam-applications/${id}`);
      } else if (type === "PERSONALIZED_ASSIGNMENT") {
        // The same solver route the /homeworks card opens.
        navigate(`/personalized-assignments/${id}`);
      } else if (type === "SUPPORT_ASSIGNMENT") {
        navigate(`/support-assignments/${id}`);
      }
    };
    return (
      <div
        className="qak-item"
        // Type-scoped: a bireysel ödev's id and a şube ödevi's id can collide numerically.
        key={`${type}-${id}`}
        style={{
          borderLeft: `4px solid ${s.ink}`,
          background: retrieveSubjectCardBackground(coterieType),
        }}
      >
        <span className="qak-item-pill">
          <SubjectIcon coterieType={coterieType} size={52} />
        </span>
        <div className="qak-item-body">
          {/* Meta rail — KIND left, STATUS right, matching the /homeworks card's
              .qdd-hw-rail so one kind vocabulary reads the same on every surface.
              No hairline here, unlike the two card grids: this row's body starts at the
              52px emblem's right edge, so a rule would span only part of the card.
              The status is always produced, so the rail can never render empty. */}
          <div className="qak-item-rail">
            <AssignmentKindChip type={type} />
            <span className={`qak-status qak-status--${statusKind}`}>
              {isUpcoming
                ? i18n.t("homework_status_upcoming")
                : isExpired
                  ? i18n.t("homework_ended_short")
                  : i18n.t(completionStatus)}
            </span>
          </div>
          <div className="qak-item-meta">
            <span className="qak-item-subject" style={{ color: s.ink }}>
              {coterieType ? i18n.t(coterieType) : ""}
            </span>
            {name ? <span className="qak-item-topic">{name}</span> : null}
          </div>
          <div className="qak-bar">
            <div
              className="qak-bar-fill"
              style={{ width: `${pct}%`, background: s.ink }}
            />
          </div>
          <div className="qak-item-foot">
            <span className="qak-item-sub">
              {done} / {total} {i18n.t("activity")}
            </span>
            {isUpcoming && beginAt ? (
              <span className="qak-item-begins">
                <Icon name="clock" size={12} />
                <LiveCountdown
                  targetAt={beginAt}
                  variant="compact"
                  prefixKey="begins_in"
                  onComplete={() => setUnlockTick((tick) => tick + 1)}
                />
              </span>
            ) : null}
          </div>
        </div>
        <div className="qak-item-side">
          {isUpcoming ? null : (
            <>
              {/* A bireysel ödev carries no end date — an absent endAt must render NOTHING, not
                  the "Invalid Date" new Date(undefined) prints. */}
              {endAt ? (
                <span className="qak-item-due">
                  <Icon name="clock" size={13} />
                  {endDate.toLocaleDateString(language, {
                    day: "numeric",
                    month: "short",
                  })}
                </span>
              ) : null}
              {isExpired ? null : completionStatus === "NOT_STARTED" ? (
                <PrimaryButton
                  {...({
                    title: i18n.t("START"),
                    size: "compact",
                    onClick: goToContent,
                  } as any)}
                />
              ) : completionStatus === "ON_GOING" ? (
                <SecondaryButton
                  {...({
                    title: i18n.t("continue"),
                    size: "unlimited",
                    textOnly: true,
                    onClick: goToContent,
                  } as any)}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="qak-root" data-tour={dataTour}>
      <style>{homeworkStyles}</style>
      <div className="qak-header">
        <div className="qak-title-group">
          <Icon name="homework" size={20} />
          <p className="qak-title">{i18n.t("homeworks")}</p>
        </div>
        <div className="qak-see-all" onClick={() => navigate("/homeworks")}>
          <p className="qak-see-all-text">{i18n.t("see_all")} →</p>
        </div>
      </div>
      {loading ? (
        <BaseLoading
          {...({
            size: "large",
            primaryColor: "#57C2D3",
            secondaryColor: "#57C2D3",
          } as any)}
        />
      ) : contentList.length === 0 ? (
        <EmptyState
          compact
          icon="clipboardText"
          title={i18n.t("no_active_assignment_applications")}
        />
      ) : (
        <div className="qak-list no-scrollbar">
          {contentList.map((content) => renderRow(content))}
        </div>
      )}
    </div>
  );
};

export default Homework;
